import { type Conversation } from "shared/apiClient";
import { AssessmentLights } from "shared/types";
import { getText, interpolate } from "shared/lib";

import { useCouncilLocalisation } from "shared/hooks";

import { ExternalLinkIcon } from "shared/components";

export interface MVProps {
  assessmentLights: string;
  conversation: Conversation | undefined | null;
}

export function MV({ assessmentLights, conversation }: MVProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const renderRequestFormalAdvice = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request formal advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "To receive a written response reviewed by a delegated Council officer, you should submit a request at the link below. This provides you with written confirmation on whether a planning permit is required for your proposal.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/planning-applications-and-permits/prepare-for-planning-application/#need-a-planning-permit"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRequestFormalAdvice3 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request formal advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you wish to have this report and your proposal reviewed by a delegated officer, you should submit a request at the link below. This provides you with written confirmation on whether a planning permit is required.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/planning-applications-and-permits/prepare-for-planning-application/#need-a-planning-permit"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderRequestFormalAdvice4 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Request formal advice")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you wish to have this report, the Prohibited result and your proposal reviewed by a delegated officer, you should submit a request at the link below. This provides you with written confirmation on whether a planning permit is allowed for this concept.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/planning-applications-and-permits/prepare-for-planning-application/#need-a-planning-permit"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request advice")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderReviewBuildingRequirements1 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Review Building requirements")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Although you may not require a planning permit, often Building requirements still apply - use Council's web content to review these requirements before proceeding.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/building-information/"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Building requirements")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderReviewBuildingRequirements2 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Review Building requirements")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you do not require a planning permit, often Building requirements still apply - use Council's web content to review these requirements before proceeding.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/building-information/"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Building requirements")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDownloadCopyOfTitle = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Download a copy of title")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "This tool doesn't consider covenants, caveats and easements which can impact what you are allowed to do. A Certificate of Title includes this information which you can request, pay for and download online.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://www.landata.online/"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Request a Certificate of Title")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderDetermineInsufficientInfo = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Determine 'insufficient information'")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Some information is unclear and may allow for exemptions to requiring a permit; once you have reviewed where this report has 'insufficient information', measure that against the permit requirements.",
          )}
        </p>
      </div>
    );
  };

  const renderArrangePreApplicationMeeting = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Arrange a pre-application meeting")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "In order to best prepare to submit a complete and decision-ready application, Council provides a pre-application meeting service. To take advantage of that, you may submit a request on the link below.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/planning-applications-and-permits/prepare-for-planning-application/#book-preapplication-meeting"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Arrange meeting")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderArrangePreApplicationMeeting4 = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Arrange a pre-application meeting")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "Although a Prohibited land use can be frustrating, a conversation with a Council planner may assist in understanding your options. Book a meeting at the link below for a conversation.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/planning-applications-and-permits/prepare-for-planning-application/#book-preapplication-meeting"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Arrange meeting")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  const renderPrepareAndSubmitApplication = () => {
    return (
      <div>
        <h3 className="text-headingmd">
          {getText("Prepare and submit application")}
        </h3>
        <p className="text-bodymd mb-2">
          {getText(
            "If you are ready to submit your planning application, you can connect into Council's website and submit your application.",
          )}
        </p>
        <a
          className="text-textLink hover:text-textLinkHover text-bodymd inline-flex cursor-pointer underline"
          href="https://mvcc.vic.gov.au/work/my-development/planning-applications-and-permits/submit-your-planning-application/"
          rel="noreferrer"
          tabIndex={0}
          target="_blank"
        >
          {getText("Submit application")}
          <ExternalLinkIcon className="ml-2 mt-[3px]" />
        </a>
      </div>
    );
  };

  switch (assessmentLights) {
    case AssessmentLights.NoPermitRequired:
      return (
        <>
          {renderRequestFormalAdvice()}
          {renderReviewBuildingRequirements1()}
          {renderDownloadCopyOfTitle()}
        </>
      );
    case AssessmentLights.MoreInformationRequired:
      return (
        <>
          {renderDetermineInsufficientInfo()}
          {renderRequestFormalAdvice()}
          {renderReviewBuildingRequirements2()}
          {renderDownloadCopyOfTitle()}
        </>
      );
    case AssessmentLights.RequiresPermit:
      return (
        <>
          {renderArrangePreApplicationMeeting()}
          {renderPrepareAndSubmitApplication()}
          {renderRequestFormalAdvice3()}
          {renderDownloadCopyOfTitle()}
        </>
      );
    case AssessmentLights.Prohibited:
      return (
        <>
          {renderArrangePreApplicationMeeting4()}
          {renderRequestFormalAdvice4()}
          {renderDownloadCopyOfTitle()}
        </>
      );
  }
}
